import { useState } from 'react';
import { Typography, Input, Upload, message } from 'antd';
import {
  CameraOutlined,
  DeleteOutlined,
  SaveOutlined,
  ShopOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';

export default function BusinessInfoSection() {
  const { t } = useTranslation(['common', 'owner']);
  const { selectedBusiness, updateSelectedBusiness } = useBusinessStore();
  if (!selectedBusiness) return null;

  const [businessName, setBusinessName] = useState(selectedBusiness.businessName);
  const [welcomeMessage, setWelcomeMessage] = useState(selectedBusiness.welcomeMessage || '');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const hasNameChange = businessName.trim() !== selectedBusiness.businessName;
  const hasWelcomeChange = (welcomeMessage || '') !== (selectedBusiness.welcomeMessage || '');
  const hasChanges = hasNameChange || hasWelcomeChange;

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};
      if (hasNameChange && businessName.trim().length >= 2) patch.businessName = businessName.trim();
      if (hasWelcomeChange) patch.welcomeMessage = welcomeMessage.trim() || null;
      const updated = await businessApi.updateTenant(selectedBusiness.id, patch);
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('common:status.saved'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message ?? t('common:errors.saveFailed'));
      telegram.hapticFeedback.notification('error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) { void message.error(t('common:errors.logoTooLarge')); return false; }
    if (!['image/jpeg', 'image/png'].includes(file.type)) { void message.error(t('common:errors.logoInvalidFormat')); return false; }
    setUploadingLogo(true);
    try {
      const updated = await businessApi.uploadLogo(selectedBusiness.id, file);
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('owner:settings.businessInfo.logoUploaded'));
    } catch {
      void message.error(t('common:errors.logoUploadFailed'));
    } finally {
      setUploadingLogo(false);
    }
    return false;
  };

  const handleDeleteLogo = async () => {
    try {
      const updated = await businessApi.deleteLogo(selectedBusiness.id);
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
    } catch {
      void message.error(t('common:errors.logoDeleteFailed'));
    }
  };

  return (
    <div style={{ borderRadius: 16, background: 'var(--bl-surface, #fff)', border: '1px solid var(--bl-divider, #ebebeb)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--bl-divider, #ebebeb)' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--bl-accent-soft, #e7f1fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--bl-accent, #3390ec)' }}>
          <ShopOutlined />
        </div>
        <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
          {t('owner:settings.businessInfo.title')}
        </Typography.Text>
      </div>

      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, flexShrink: 0, background: selectedBusiness.logoUrl ? 'transparent' : 'var(--bl-accent-soft, #e7f1fe)', border: '1px solid var(--bl-divider, #ebebeb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: 'var(--bl-accent, #3390ec)', overflow: 'hidden' }}>
            {selectedBusiness.logoUrl
              ? <img src={selectedBusiness.logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : selectedBusiness.businessName.charAt(0).toUpperCase()}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Upload showUploadList={false} beforeUpload={handleLogoUpload} accept="image/jpeg,image/png">
              <button type="button" disabled={uploadingLogo} style={{ height: 34, borderRadius: 10, padding: '0 14px', background: 'var(--bl-accent-soft, #e7f1fe)', border: 'none', color: 'var(--bl-accent, #3390ec)', fontSize: 13, fontWeight: 600, cursor: uploadingLogo ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, opacity: uploadingLogo ? 0.6 : 1 }}>
                <CameraOutlined /> {uploadingLogo ? t('owner:settings.businessInfo.logoUploading') : t('owner:settings.businessInfo.logoButton')}
              </button>
            </Upload>
            {selectedBusiness.logoUrl && (
              <button type="button" onClick={() => void handleDeleteLogo()} style={{ width: 34, height: 34, borderRadius: 10, background: '#fef2f2', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#ef4444' }}>
                <DeleteOutlined />
              </button>
            )}
          </div>
        </div>

        {/* Business name */}
        <div>
          <Typography.Text style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)', marginBottom: 6 }}>
            {t('owner:settings.businessInfo.businessNameLabel')}
          </Typography.Text>
          <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} maxLength={255} style={{ borderRadius: 12, height: 40 }} />
        </div>

        {/* Welcome message */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
            <MessageOutlined style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }} />
            <Typography.Text style={{ fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)' }}>
              {t('owner:settings.businessInfo.welcomeMessageLabel')}
            </Typography.Text>
          </div>
          <Input.TextArea value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} maxLength={500} rows={2} placeholder={t('owner:settings.businessInfo.welcomeMessagePlaceholder')} showCount style={{ borderRadius: 12 }} />
        </div>

        {/* Save button */}
        {hasChanges && (
          <button type="button" onClick={() => void handleSave()} disabled={saving} style={{ width: '100%', height: 46, borderRadius: 12, background: saving ? 'var(--bl-accent-soft, #e7f1fe)' : 'var(--bl-accent, #3390ec)', border: 'none', color: saving ? 'var(--bl-accent, #3390ec)' : '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1 }}>
            <SaveOutlined /> {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        )}
      </div>
    </div>
  );
}
